import type { CanvasInputEvent, EventOrigin } from './types';

const POINTER_EVENT_TYPE_MAP = {
  pointerdown: 'pointerDown',
  pointermove: 'pointerMove',
  pointerup: 'pointerUp',
  pointercancel: 'pointerCancel',
} as const;

const isPointerEventType = (
  type: string
): type is keyof typeof POINTER_EVENT_TYPE_MAP =>
  type in POINTER_EVENT_TYPE_MAP;

export const createPointerInputEvent = (
  event: PointerEvent,
  origin: EventOrigin
): CanvasInputEvent | null => {
  if (!isPointerEventType(event.type)) return null;

  return {
    type: POINTER_EVENT_TYPE_MAP[event.type],
    pointerId: event.pointerId,
    pointerType: event.pointerType,
    rawEvent: event,
    clientX: event.clientX,
    clientY: event.clientY,
    origin,
  };
};

export const createWheelInputEvent = (
  event: WheelEvent,
  origin: EventOrigin
): CanvasInputEvent => ({
  type: 'wheel',
  rawEvent: event,
  deltaX: event.deltaX,
  deltaY: event.deltaY,
  deltaMode: event.deltaMode,
  clientX: event.clientX,
  clientY: event.clientY,
  origin,
});

export const createKeyInputEvent = (
  event: KeyboardEvent,
  type: 'keyDown' | 'keyUp',
  origin: EventOrigin
): CanvasInputEvent => ({
  type,
  rawEvent: event,
  key: event.key,
  code: event.code,
  ctrlKey: event.ctrlKey,
  shiftKey: event.shiftKey,
  altKey: event.altKey,
  metaKey: event.metaKey,
  origin,
});

export const isPointerEvent = (
  event: CanvasInputEvent
): event is Extract<
  CanvasInputEvent,
  { type: 'pointerDown' | 'pointerMove' | 'pointerUp' | 'pointerCancel' }
> => {
  return (
    event.type === 'pointerDown' ||
    event.type === 'pointerMove' ||
    event.type === 'pointerUp' ||
    event.type === 'pointerCancel'
  );
};

export const isWheelEvent = (
  event: CanvasInputEvent
): event is Extract<CanvasInputEvent, { type: 'wheel' }> => {
  return event.type === 'wheel';
};

/** @public */
export const isKeyEvent = (
  event: CanvasInputEvent
): event is Extract<CanvasInputEvent, { type: 'keyDown' | 'keyUp' }> => {
  return event.type === 'keyDown' || event.type === 'keyUp';
};

export const resolveEventOrigin = (
  event: Event,
  viewport: Element | null
): EventOrigin => ({
  insideCanvas:
    viewport !== null &&
    event.target instanceof Node &&
    viewport.contains(event.target),
});

export const insideCanvas = (event: CanvasInputEvent): boolean =>
  event.origin.insideCanvas;
